import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Hidden,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { PropTypes } from "prop-types";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

//... Common imports
import { AppDataContext } from "../../common/appContext";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";

//... Service imports
import { UserDataService } from "../../services";
import { baseUrl } from "../../services/AzureStorage";
import { LeaderDashboardService } from "../../services/leaderDashboard.Service";

//... Configs
import { isShowUnitDrawer } from "../../configs/AppSettings";
import { isShowUnitDrawerItem } from "../../configs/AppSettings";

//... Shared Component imports
import { exportDataToExcel } from "../../sharedComponents/CustomExportToExcel";
import UnitFilterDrawer from "../../sharedComponents/UnitFilterDrawer";
import AppNaviGator from "../../sharedComponents/appNavigator/AppNaviGator";

//... Local imports
import LeaderBoard from "./LeaderBoard";
import NbaLoader from "../../sharedComponents/loader/NbaLoader";
import FiveOnFiveGameLeaders from "./FiveOnFiveGameLeaders";

const useStyles = makeStyles((theme) => ({
  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerBox: {
    background: "#c1d0ed",
    borderRadius: "0",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      "& > div": {
        flexDirection: "column",
        "& > div": {
          padding: theme.spacing(0),
        },
      },
    },
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  stickyColumn: {
    position: "sticky",
    background: "white",
    left: 0,
    paddingRight: theme.spacing(2),
  },
  tableViewContainer: {
    maxHeight: 450,
    overflow: "auto",
  },
  tableViewWrapper: {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  stickyTableViewHead: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#fff",
  },
  selectedEvent: {
    borderBottom: "10px solid #c91330",
    boxShadow: "-1px -1px 10px #000 !important",
    maxWidth: "345px",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  eventCard: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  navigationLinkContainer: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
    "& > div": {
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        display: "flex",
        flex: "1",
      },
    },
  },
  navigationLink: {
    color: "#1d428a",
    cursor: "pointer",
    background: "#fff",
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0, 0, 0, 2),
    "& .MuiButton-endIcon": {
      position: "relative",
      background: "#1d428a",
      padding: theme.spacing(2, 2, 2, 3), //'15px 16px 15px 24px',
      color: "#fff",
      display: "inline-flex",
      verticalAlign: "middle",
      alignItems: "center",
      borderRadius: theme.spacing(0, 0.5, 0.5, 0), //'0px 4px 4px 0px',
      "&::before": {
        content: '""',
        position: "absolute",
        border: "none",
        borderLeft: "20px solid #ffffff",
        left: "-1px",
        zIndex: "1",
        top: "0",
        width: "0",
        borderBottom: "52px solid transparent",
        height: "0",
      },
    },
    "&:hover": { backgroundColor: "#fff" },
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flex: "1",
      justifyContent: "flex-start",
    },
    "& .MuiTypography-root": {
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        flex: "1",
      },
    },
  },
  normalScreenContainer: {
    [theme.breakpoints.between("sm", "xl")]: {
      marginTop: "10rem",
    },
    [theme.breakpoints.up("xl")]: {
      marginTop: "6rem",
    },
  },
  excelBox: {
    [theme.breakpoints.down("sm")]: {
      "& span": {
        display: "none",
      },
    },
  },
  exportToExcel: {
    backgroundColor: "white",
    minWidth: "50px",
    "&:hover": {
      backgroundColor: "white",
    },
    textTransform: "none",
    border: "1px solid lightgrey",
    boxShadow: "1px 1px 3px 1px lightgrey",
  },
  appbar: {
    backgroundColor: "#f5f5f5",
    color: "#000000",
    "& .Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
}));

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function HomeContainer() {
  let navigate = useNavigate();
  const classes = useStyles();
  const theme = useTheme();
  const {
    user,
    getActiveEventId,
    selectedEvent,
    setSelectedEvent,
    selectedEventId,
    //selectedEventId,
    //setSelectedEventId
    setSelectedCombineResultsTab,
    lengthUnits,
    weightUnits,
    setWeightUnits,
    setLengthUnits,
    getWeightUnitValue,
    getLengthUnitValue,
    getLengthUnitLabelType,
    getWeightUnitLabelType,
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    isShowDEXABodyFat,
    loadContext,
  } = useContext(AppDataContext);
  const [eventId, setEventId] = useState(selectedEventId);
  let userAppRoles = user.appRoles || [];
  const [loading, setLoading] = useState(false);
  const [balchecked, setIsbalchecked] = useState(true);
  const [checkEvent, setCheckEvent] = useState(true);
  const [loadingshootingdrill, setLoadingshootingdrill] = useState(false);
  const [loadingsaa, setLoadingsaa] = useState(false);

  const [favPlayersASAData, setfavPlayersASAData] = useState([]);
  const [favPlayersShootingDrillData, setfavPlayersShootingDrillData] =
    useState([]);

  //... Tabs state holder
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (isShowUnitDrawer) {
      setLengthUnits(4);
      setWeightUnits(1);
    }
  }, []);

  useEffect(() => {
    //console.log("Approles", userAppRoles);
    if (sessionStorage.getItem("checkUserAndNavigation") === "true") {
      UserDataService.redirectToTeamDashboard().then((bRedirect) => {
        if (bRedirect) navigate(RouteConstants.TEAM_BALLOT_DASHBOARD);
      });
    }
  }, []);

  useEffect(() => {
    setEventId(selectedEventId);
    var eid = selectedEventId.slice(-2);
    if (eid === "11") {
      setCheckEvent(true);
      setIsbalchecked(true);
    } else {
      setCheckEvent(false);
      setIsbalchecked(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (eventId != 0 && user && user.loginID && user.userName) {
      // setLoading(true);
      GetShootingDrillLeaderBoardByLoggedInFavPlayers();
      GetLeaderboardAnthroSAAgilityByLoggedInFavPlayers();
    } else {
      //setLoading(true);
      // getActiveEventId();
      if (Object.keys(user).length === 0) loadContext();
    }
  }, [eventId, user?.loginID]);

  //...Handlers
  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleChangeIndex = (index) => {
    setSelectedTab(index);
  };

  const GetShootingDrillLeaderBoardByLoggedInFavPlayers = async () => {
    setLoadingshootingdrill(true);
    let data =
      await LeaderDashboardService.getShootingDrillLeaderBoardByLoggedInFavPlayers(
        eventId,
        user.loginID
      );
    setfavPlayersShootingDrillData(data.data);
    setLoadingshootingdrill(false);
  };

  const GetLeaderboardAnthroSAAgilityByLoggedInFavPlayers = async () => {
    setLoadingsaa(true);
    let data =
      await LeaderDashboardService.getLeaderboardAnthroSAAgilityByLoggedInFavPlayers(
        eventId,
        user.userName
      );
    setfavPlayersASAData(data.data);
    setLoadingsaa(false);
  };

  const viewPlayerProfile = (playerId) => {
    navigate(`${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "HomeContainer" },
    });
  };

  const exportToExcelHandler = async () => {
    const sheetsData = [
      {
        sheetName: "Anthros And Strength Agility",
        tables: getAnthrosAndSASFavoritePlayersTables(),
      },
      {
        sheetName: "Shooting Drills",
        tables: getShootingDrillsFavoritePlayersTables(),
      },
    ];

    await exportDataToExcel(sheetsData, "Favorite Player Leader Boards");
  };

  const getAnthrosAndSASFavoritePlayersTables = () => {
    const styleHeaderCss = {
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "e6eefb" },
      },
      font: { sz: "11", bold: true, color: { rgb: "1d428a" } },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
      alignment: {
        wrapText: true,
        horizontal: "center",
        vertical: "top",
      },
    };

    const columnCount = isShowDEXABodyFat ? 15 : 14;

    return [
      {
        title: "",
        headerNames: [
          {
            title: "Player Name",
            style: styleHeaderCss,
            width: "30",
          },
          {
            title: `Standing Reach ${getLengthUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "30",
          },
          {
            title: `Wingspan Dimension ${getLengthUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
          // {
          //   title: `Height With Shoes ${getLengthUnitLabelType(lengthUnits)}`,
          //   style: styleHeaderCss,
          //   width: "20",
          // },
          {
            title: `Height Without Shoes ${getLengthUnitLabelType(
              lengthUnits
            )}`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Weight ${getWeightUnitLabelType(weightUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Hand Width ${getHandDimensionsUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Hand Length ${getHandDimensionsUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
          // {
          //   title: `Body Fat`,
          //   style: styleHeaderCss,
          //   width: "20",
          // },
          // ...(isShowDEXABodyFat === "true"
          //   ? [
          //       {
          //         title: "DXEA Body Fat",
          //         style: styleHeaderCss,
          //         width: "20",
          //       },
          //     ]
          //   : []),
          {
            title: "Lane Shuttle Left",
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: "Lane Shuttle Right",
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: "3/4 Court Sprint",
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: "Pro Lane",
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Max Vertical Jump ${getLengthUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Standing Vertical ${getLengthUnitLabelType(lengthUnits)}`,
            style: styleHeaderCss,
            width: "20",
          },
        ],
        data:
          favPlayersASAData && favPlayersASAData.length > 0
            ? favPlayersASAData.map((record) => {
                var height = "";
                var heightwo = "";
                var sr = "";
                var wingspan = "";

                if (
                  record.heightWithShoesFeet !== "" &&
                  record.heightWithShoesInches !== ""
                ) {
                  height = getLengthUnitValue(
                    record.heightWithShoesFeet,
                    record.heightWithShoesInches
                  );
                }
                if (height === "0'0''") {
                  height = "";
                }
                if (
                  record.heightWithOutShoesFeet !== "" &&
                  record.heightWithOutShoesInches !== ""
                ) {
                  heightwo = getLengthUnitValue(
                    record.heightWithOutShoesFeet,
                    record.heightWithOutShoesInches
                  );
                }
                if (heightwo === "0'0''") {
                  heightwo = "";
                }
                if (
                  record.standingReachFeet !== "" &&
                  record.standingReachInches !== ""
                ) {
                  sr = getLengthUnitValue(
                    record.standingReachFeet,
                    record.standingReachInches
                  );
                }
                if (sr === "0'0''") {
                  sr = "";
                }
                if (
                  record.wingSpanFeet !== "" &&
                  record.wingSpanInches !== ""
                ) {
                  wingspan = getLengthUnitValue(
                    record.wingSpanFeet,
                    record.wingSpanInches
                  );
                }
                if (wingspan === "0'0''") {
                  wingspan = "";
                }

                return {
                  playerName: record.playerName ? record.playerName : "",
                  sr: sr,
                  wingspan: wingspan,
                  // height: height,
                  heightwo: heightwo,
                  bodyWeight: record.bodyWeight
                    ? getWeightUnitValue(record.bodyWeight)
                    : 0,
                  handwidth: record.handWidth
                    ? getHandDimensionsUnitValue(record.handWidth)
                    : 0,
                  handLength: record.handLength
                    ? getHandDimensionsUnitValue(record.handLength)
                    : 0,
                  // bodyFat: record.bodyFat ? record.bodyFat : "-",
                  // ...(isShowDEXABodyFat === "true"
                  //   ? {
                  //       bodyFatDXA: record.bodyFatDXA ? record.bodyFatDXA : "-",
                  //     }
                  //   : ""),
                  shuttleRunLeftTrialTime: record.shuttleRunLeftTrialTime
                    ? record.shuttleRunLeftTrialTime
                    : "-",
                  shuttleRunRightTrialTime: record.shuttleRunRightTrialTime
                    ? record.shuttleRunRightTrialTime
                    : "-",
                  threeFourRunTime: record.threeFourRunTime
                    ? record.threeFourRunTime
                    : "-",
                  proLaneTrialTime: record.proLaneTrialTime
                    ? record.proLaneTrialTime
                    : "-",
                  maxVerticalLeap: record.maxVerticalLeap
                    ? getHandDimensionsUnitValue(record.maxVerticalLeap)
                    : 0,
                  standingVerticalLeap: record.standingVerticalLeap
                    ? getHandDimensionsUnitValue(record.standingVerticalLeap)
                    : 0,
                };
              })
            : [],

        columnStyles: Array(columnCount).fill({
          font: { size: 11 },
          width: 40,
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
          },
        }),
      },
    ];
  };

  const getShootingDrillsFavoritePlayersTables = () => {
    const styleHeaderCss = {
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "e6eefb" },
      },
      font: { sz: "11", bold: true, color: { rgb: "1d428a" } },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
      alignment: {
        wrapText: true,
        horizontal: "center",
        vertical: "top",
      },
    };

    return [
      {
        title: "",
        headerNames: [
          {
            title: `Player Name`,
            style: styleHeaderCss,
            width: "30",
          },
          {
            title: `Spot Up Shooting`,
            style: styleHeaderCss,
            width: "30",
          },
          {
            title: `Side Mid Side Drill`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Mid Side Mid Drill`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Off The Dribble Shooting`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Free Throws`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `3 Point Star Drill`,
            style: styleHeaderCss,
            width: "20",
          },
          {
            title: `Mid Range Star Drill`,
            style: styleHeaderCss,
            width: "20",
          },
        ],
        data:
          favPlayersShootingDrillData && favPlayersShootingDrillData.length > 0
            ? favPlayersShootingDrillData.map((record) => {
                return {
                  playerName: record.playerName ? record.playerName : "",
                  noOfMadeSpotup: `${record.noOfMadeSpotup ?? 0}/${
                    record.maxCountSpotUp ?? 0
                  }`,
                  noOfMadeSideMidSide: `${record.noOfMadeSideMidSide ?? 0}/${
                    record.maxCountSideMidSide ?? 0
                  }`,
                  noOfMadeMidSideMid: `${record.noOfMadeMidSideMid ?? 0}/${
                    record.maxCountMidSideMid ?? 0
                  }`,
                  noOfMadeOffTheDribble: `${
                    record.noOfMadeOffTheDribble ?? 0
                  }/${record.maxCountOffTheDribble ?? 0}`,
                  noOfMadeFreeThrows: `${record.noOfMadeFreeThrows ?? 0}/${
                    record.maxCountFreeThrows ?? 0
                  }`,
                  noOfMade3PointStar: `${record.noOfMade3PointStar ?? 0}/${
                    record.maxCount3PointStar ?? 0
                  }`,
                  noOfMadeMidRangeStar: `${record.noOfMadeMidRangeStar ?? 0}/${
                    record.maxCountMidRangeStar ?? 0
                  }`,
                };
              })
            : [],

        columnStyles: Array(8).fill({
          font: { size: 11 },
          width: 40,
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
          },
        }),
      },
    ];
  };

  if (!user?.loginID) {
    return <NbaLoader />;
  }

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          display: { xs: "none", sm: "block" },
          top: "64px",
          background: "#ebebeb",
          boxShadow: "none",
          zIndex: "1098",
        }}
      >
        <Container maxWidth={false}>
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            className={classes.eventCard}
          >
            <AppNaviGator
              userAppRoles={userAppRoles}
              user={user}
              eventId={eventId}
            />
          </Box>
        </Container>
      </AppBar>

      <Container maxWidth={false} className={classes.normalScreenContainer}>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          className={classes.eventCard}
          sx={{ display: { xs: "block", sm: "none" } }}
        >
          <AppNaviGator
            userAppRoles={userAppRoles}
            user={user}
            eventId={eventId}
          />
        </Box>

        <Grid container pt={2} pb={2}>
          <Grid item lg xs={12}>
            <Paper
              elevation={8}
              sx={{
                borderRadius: "16px",
              }}
            >
              <Box
                component="div"
                display="flex"
                flexGrow={1}
                flexDirection="column"
              >
                <AppBar
                  position="static"
                  className={classes.appbar}
                  sx={{
                    borderTopLeftRadius: "15px !important",
                    borderTopRightRadius: "15px !important",
                  }}
                >
                  <Hidden mdUp>
                    <Tabs
                      scrollButtons
                      allowScrollButtonsMobile
                      value={selectedTab}
                      onChange={handleChangeTab}
                      indicatorColor="primary"
                      textColor="inherit"
                      variant="scrollable"
                      aria-label="full width tabs example"
                    >
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="Leader Boards"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="Favorite Players"
                        {...a11yProps(1)}
                      />
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="5 On 5 Games"
                        {...a11yProps(2)}
                      />
                    </Tabs>
                  </Hidden>
                  <Hidden mdDown>
                    <Tabs
                      scrollButtons
                      allowScrollButtonsMobile
                      value={selectedTab}
                      onChange={handleChangeTab}
                      indicatorColor="primary"
                      textColor="inherit"
                      variant="standard"
                      aria-label="full width tabs example"
                    >
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="Leader Boards"
                        {...a11yProps(0)}
                      ></Tab>
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="Favorite Players"
                        {...a11yProps(1)}
                      />
                      <Tab
                        sx={{ textTransform: "none" }}
                        label="5 On 5 Games"
                        {...a11yProps(2)}
                      />
                    </Tabs>
                  </Hidden>
                </AppBar>
                <SwipeableViews
                  className={classes.swipeView}
                  axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                  index={selectedTab}
                  onChangeIndex={handleChangeIndex}
                >
                  {/* Tab 1 */}
                  <TabPanel value={selectedTab} index={0} dir={theme.direction}>
                    <LeaderBoard />
                  </TabPanel>

                  {/* Tab 2 */}
                  <TabPanel value={selectedTab} index={1} dir={theme.direction}>
                    <Box sx={{ m: 2 }}>
                      <Paper elevation={2} sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          className={classes.headerBox}
                        >
                          <Box pl={2}>
                            <Typography
                              className={`${classes.headerText} headerTitle`}
                            >
                              Favorite Player Anthros And Strength And Agility
                              Leader Boards
                            </Typography>
                          </Box>
                          {isShowUnitDrawerItem && (
                            <UnitFilterDrawer
                              lengthUnits={lengthUnits}
                              weightUnits={weightUnits}
                              setLengthUnits={setLengthUnits}
                              setWeightUnits={setWeightUnits}
                            />
                          )}

                          <Box pr={2}>
                            <Button
                              size="small"
                              className={classes.exportToExcel}
                              title="Export to Excel"
                              sx={{
                                border: "1px solid lightgrey",
                                boxShadow: "1px 1px 3px black",
                                minWidth: "auto !important",
                              }}
                              onClick={exportToExcelHandler}
                            >
                              <img
                                alt="pngImg"
                                src="../../../../playerProfile/excel.png"
                                width="26"
                                height="26"
                              />
                            </Button>
                          </Box>
                        </Box>
                        <Grid container spacing={2} pt={3}>
                          {loadingsaa ? (
                            <TableContainer
                              sx={{ textAlign: "center", minHeight: "50px" }}
                            >
                              <CircularProgress />
                            </TableContainer>
                          ) : (
                            <TableContainer
                              className={classes.tableViewContainer}
                            >
                              {favPlayersASAData.length > 0 ? (
                                favPlayersASAData &&
                                favPlayersASAData.length > 0 && (
                                  <Table className={classes.tableViewWrapper}>
                                    <TableHead
                                      className={classes.stickyTableViewHead}
                                    >
                                      <TableRow>
                                        <TableCell
                                          className={classes.stickyColumn}
                                          sx={{
                                            padding: "5px",
                                            paddingLeft: "25px",
                                            width: "12%",
                                          }}
                                        >
                                          Player Name
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Standing Reach
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Wingspan Dimension
                                        </TableCell>
                                        {/* Note:Commenting code client requirement */}
                                        {/* <TableCell
                            style={{
                              padding: "5px",
                              textAlign: "center",
                              verticalAlign: "top",
                            }}
                          >
                            Height With Shoes
                          </TableCell> */}
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Height W/o Shoes
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Weight
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Hand Dimensions{" "}
                                        </TableCell>
                                        {/* Note: Based on restrictions from the legal team. */}
                                        {/* <TableCell
                            style={{ padding: "5px", textAlign: "center" }}
                          >
                            Body Fat
                          </TableCell>
                          <TableCell
                            style={{
                              padding: "5px",
                              textAlign: "center",
                              display:
                                isShowDEXABodyFat === "true" ? "" : "none",
                            }}
                          >
                            DEXA Body Fat
                          </TableCell> */}
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Lane Shuttle Left
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Lane Shuttle Right
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          3/4 Court Sprint
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Pro Lane
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Max Vertical Jump
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            verticalAlign: "top",
                                          }}
                                        >
                                          Standing Vertical
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell
                                          className={classes.stickyColumn}
                                        ></TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getLengthUnitLabelType(lengthUnits)}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getLengthUnitLabelType(lengthUnits)}
                                        </TableCell>
                                        {/* <TableCell
                            style={{ padding: "5px", textAlign: "center" }}
                          >
                            {getLengthUnitLabelType(lengthUnits)}
                          </TableCell> */}
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getLengthUnitLabelType(lengthUnits)}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getWeightUnitLabelType(weightUnits)}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                            fontSize: "12px",
                                          }}
                                        >
                                          W{" "}
                                          {getHandDimensionsUnitLabelType(
                                            lengthUnits
                                          )}{" "}
                                          | L{" "}
                                          {getHandDimensionsUnitLabelType(
                                            lengthUnits
                                          )}
                                        </TableCell>
                                        {/* Note: Based on restrictions from the legal team. */}
                                        {/* <TableCell
                            style={{ padding: "5px", textAlign: "center" }}
                          >
                            (%)
                          </TableCell>
                          <TableCell
                            style={{
                              padding: "5px",
                              textAlign: "center",
                              display:
                                isShowDEXABodyFat === "true" ? "" : "none",
                            }}
                          >
                            (%)
                          </TableCell> */}
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          (sec)
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          (sec)
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          (sec)
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          (sec)
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getHandDimensionsUnitLabelType(
                                            lengthUnits
                                          )}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {getHandDimensionsUnitLabelType(
                                            lengthUnits
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {favPlayersASAData.map((val, index) => {
                                        return (
                                          <TableRow key={index}>
                                            <TableCell
                                              className={classes.stickyColumn}
                                              sx={{
                                                padding: "5px",
                                              }}
                                            >
                                              <Box display="flex">
                                                {val.headshotURL &&
                                                val.headshotURL != "" ? (
                                                  <Avatar
                                                    className={classes.smBlue}
                                                    sx={{
                                                      bgcolor: "#1d428a",
                                                      cursor: "pointer",
                                                    }}
                                                    src={
                                                      val.headshotURL
                                                        ? baseUrl(
                                                            val.headshotURL
                                                          )
                                                        : ""
                                                    }
                                                    alt="basketball"
                                                    title="View Player Profile"
                                                    onClick={() =>
                                                      viewPlayerProfile(
                                                        val.playerID
                                                      )
                                                    }
                                                  >
                                                    <Typography variant="caption">
                                                      {Helper.GetNameInitials(
                                                        `${val.playerName}`
                                                      )}
                                                    </Typography>
                                                  </Avatar>
                                                ) : (
                                                  <Avatar
                                                    className={classes.smBlue}
                                                    sx={{
                                                      bgcolor: "#1d428a",
                                                      cursor: "pointer",
                                                    }}
                                                    alt="basketball"
                                                    title="View Player Profile"
                                                    onClick={() =>
                                                      viewPlayerProfile(
                                                        val.playerID
                                                      )
                                                    }
                                                  >
                                                    <Typography variant="caption">
                                                      {Helper.GetNameInitials(
                                                        `${val.playerName}`
                                                      )}
                                                    </Typography>
                                                  </Avatar>
                                                )}
                                                &nbsp;&nbsp;
                                                <Typography
                                                  variant="body2"
                                                  className={
                                                    classes.playerNameStyle
                                                  }
                                                  title="View Player Profile"
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  {val.playerName}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {getLengthUnitValue(
                                                val.standingReachFeet,
                                                val.standingReachInches
                                              )}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {getLengthUnitValue(
                                                val.wingSpanFeet,
                                                val.wingSpanInches
                                              )}
                                            </TableCell>

                                            {/* Note:Commenting code client requirement */}
                                            {/* <TableCell
                                  style={{
                                    padding: "5px",
                                    textAlign: "center",
                                  }}
                                >
                                  {getLengthUnitValue(
                                    val.heightWithShoesFeet,
                                    val.heightWithShoesInches
                                  )}
                                </TableCell> */}
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {getLengthUnitValue(
                                                val.heightWithOutShoesFeet,
                                                val.heightWithOutShoesInches
                                              )}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {getWeightUnitValue(
                                                val.bodyWeight
                                              )}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                                fontSize: "12px",
                                              }}
                                            >
                                              {getHandDimensionsUnitValue(
                                                val.handWidth
                                              )}{" "}
                                              |{" "}
                                              {getHandDimensionsUnitValue(
                                                val.handLength
                                              )}
                                            </TableCell>
                                            {/* Note: Based on restrictions from the legal team. */}
                                            {/* <TableCell
                                  style={{
                                    padding: "5px",
                                    textAlign: "center",
                                  }}
                                >
                                  {val.bodyFat ? val.bodyFat : "-"}
                                </TableCell>
                                <TableCell
                                  style={{
                                    padding: "5px",
                                    textAlign: "center",
                                    display:
                                      isShowDEXABodyFat === "true"
                                        ? ""
                                        : "none",
                                  }}
                                >
                                  {val.bodyFatDXA ? val.bodyFatDXA : "-"}
                                </TableCell> */}

                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {val.shuttleRunLeftTrialTime
                                                ? val.shuttleRunLeftTrialTime
                                                : "-"}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {val.shuttleRunRightTrialTime
                                                ? val.shuttleRunRightTrialTime
                                                : "-"}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {val.threeFourRunTime
                                                ? val.threeFourRunTime
                                                : "-"}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {val.proLaneTrialTime
                                                ? val.proLaneTrialTime
                                                : "-"}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {/* {val.maxVerticalFeet}' {val.maxVerticalInches}" */}
                                              {getHandDimensionsUnitValue(
                                                val.maxVerticalLeap
                                              )}
                                            </TableCell>
                                            <TableCell
                                              style={{
                                                padding: "5px",
                                                textAlign: "center",
                                              }}
                                            >
                                              {getHandDimensionsUnitValue(
                                                val.standingVerticalLeap
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                )
                              ) : (
                                <p style={{ textAlign: "center" }}>
                                  No Favorite Player is selected
                                </p>
                              )}
                            </TableContainer>
                          )}
                        </Grid>
                      </Paper>
                    </Box>

                    <Box sx={{ m: 2 }}>
                      <Paper elevation={2} sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          flexDirection="row"
                          className={classes.headerBox}
                        >
                          <Box display="flex" pl={2}>
                            <Typography
                              className={`${classes.headerText} headerTitle`}
                            >
                              Favorite Player Shooting Drills Leaderboard
                            </Typography>
                          </Box>
                        </Box>
                        <Grid container spacing={2} pt={3}>
                          {loadingshootingdrill ? (
                            <TableContainer
                              sx={{ textAlign: "center", minHeight: "50px" }}
                            >
                              <CircularProgress />
                            </TableContainer>
                          ) : (
                            <TableContainer
                              className={classes.tableViewContainer}
                            >
                              {favPlayersShootingDrillData.length > 0 ? (
                                favPlayersShootingDrillData &&
                                favPlayersShootingDrillData.length > 0 && (
                                  <Table className={classes.tableViewWrapper}>
                                    <TableHead
                                      className={classes.stickyTableViewHead}
                                    >
                                      <TableRow>
                                        <TableCell
                                          alignItems="center"
                                          style={{
                                            padding: "5px",
                                            paddingLeft: "25px",
                                          }}
                                        >
                                          Player Name
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Spot Up
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Side Mid Side
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Mid Side Mid
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Off The Dribble
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Free Throws
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          3 Point Star
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            padding: "5px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Mid Range Star
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {favPlayersShootingDrillData.map(
                                        (val, index) => {
                                          return (
                                            <TableRow key={index}>
                                              <TableCell
                                                style={{ padding: "5px" }}
                                              >
                                                <Box display="flex">
                                                  {val.headshotURL &&
                                                  val.headshotURL != "" ? (
                                                    <Avatar
                                                      className={classes.smBlue}
                                                      sx={{
                                                        bgcolor: "#1d428a",
                                                        cursor: "pointer",
                                                      }}
                                                      src={
                                                        val.headshotURL
                                                          ? baseUrl(
                                                              val.headshotURL
                                                            )
                                                          : ""
                                                      }
                                                      alt="basketball"
                                                      title="View Player Profile"
                                                      onClick={() =>
                                                        viewPlayerProfile(
                                                          val.playerID
                                                        )
                                                      }
                                                    >
                                                      <Typography variant="caption">
                                                        {Helper.GetNameInitials(
                                                          `${val.playerName}`
                                                        )}
                                                      </Typography>
                                                    </Avatar>
                                                  ) : (
                                                    <Avatar
                                                      className={classes.smBlue}
                                                      sx={{
                                                        bgcolor: "#1d428a",
                                                        cursor: "pointer",
                                                      }}
                                                      alt="basketball"
                                                      title="View Player Profile"
                                                      onClick={() =>
                                                        viewPlayerProfile(
                                                          val.playerID
                                                        )
                                                      }
                                                    >
                                                      <Typography variant="caption">
                                                        {Helper.GetNameInitials(
                                                          `${val.playerName}`
                                                        )}
                                                      </Typography>
                                                    </Avatar>
                                                  )}
                                                  &nbsp;&nbsp;
                                                  <Typography
                                                    variant="body2"
                                                    className={
                                                      classes.playerNameStyle
                                                    }
                                                    title="View Player Profile"
                                                    onClick={() =>
                                                      viewPlayerProfile(
                                                        val.playerID
                                                      )
                                                    }
                                                  >
                                                    {val.playerName}
                                                  </Typography>
                                                </Box>
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeSpotup}/
                                                {val.maxCountSoptUp}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeSideMidSide}/
                                                {val.maxCountSideMidSide}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeMidSideMid}/
                                                {val.maxCountMidSideMid}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeOffTheDribble}/
                                                {val.maxCountOffTheDribble}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeFreeThrows}/
                                                {val.maxCountFreeThrows}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMade3PointStar}/
                                                {val.maxCount3PointStar}
                                              </TableCell>
                                              <TableCell
                                                style={{
                                                  padding: "5px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {val.noOfMadeMidRangeStar}/
                                                {val.maxCountMidRangeStar}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                      )}
                                    </TableBody>
                                  </Table>
                                )
                              ) : (
                                <p style={{ textAlign: "center" }}>
                                  No Favorite Player is selected
                                </p>
                              )}
                            </TableContainer>
                          )}
                        </Grid>
                      </Paper>
                    </Box>
                  </TabPanel>

                  {/* Tab 2 */}
                  <TabPanel value={selectedTab} index={2} dir={theme.direction}>
                    <FiveOnFiveGameLeaders />
                  </TabPanel>
                </SwipeableViews>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
